import React, { useState } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  PriceChange as PriceChangeIcon,
  Inventory as InventoryIcon,
  VerifiedUser as VerifiedUserIcon,
  LocalShipping as ShippingIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { DashboardLayout } from '../../components/layout/DashboardLayout';

interface Alert {
  id: string;
  type: 'price' | 'stock' | 'authentication' | 'shipping' | 'critical';
  title: string;
  description: string;
  timestamp: Date;
  priority: 'high' | 'medium' | 'low';
  status: 'new' | 'read' | 'resolved';
  vehicle: {
    make: string;
    model: string;
    year: number;
  };
  partInfo: {
    partNumber: string;
    description: string;
    previousPrice?: number;
    newPrice?: number;
    stockLevel?: number;
    supplier?: string;
  };
}

export const PartsAlertsDashboard: React.FC = () => {
  const [filterType, setFilterType] = useState<string>('all');
  const [showOnlyUnread, setShowOnlyUnread] = useState(true);

  // Mock alerts data for luxury vehicles
  const alerts: Alert[] = [
    {
      id: 'AL001',
      type: 'price',
      title: 'Price Increase Alert',
      description: 'Carbon ceramic brake system price increase by 15%',
      timestamp: new Date(2024, 2, 15, 9, 30),
      priority: 'high',
      status: 'new',
      vehicle: {
        make: 'Ferrari',
        model: '488 Pista',
        year: 2024,
      },
      partInfo: {
        partNumber: 'FER-488-BP-24',
        description: 'Carbon Ceramic Brake System',
        previousPrice: 185000,
        newPrice: 212750,
        supplier: 'Ferrari South Africa',
      },
    },
    {
      id: 'AL002',
      type: 'stock',
      title: 'Limited Stock Alert',
      description: 'Only 2 units remaining in South Africa',
      timestamp: new Date(2024, 2, 15, 10, 15),
      priority: 'high',
      status: 'new',
      vehicle: {
        make: 'McLaren',
        model: '765LT',
        year: 2023,
      },
      partInfo: {
        partNumber: 'MCL-765LT-ECU',
        description: 'Engine Control Unit',
        stockLevel: 2,
        supplier: 'McLaren South Africa',
      },
    },
    {
      id: 'AL003',
      type: 'authentication',
      title: 'Authentication Required',
      description: 'Certificate of authenticity pending for imported parts',
      timestamp: new Date(2024, 2, 15, 11, 45),
      priority: 'high',
      status: 'new',
      vehicle: {
        make: 'Porsche',
        model: '911 GT3 RS',
        year: 2024,
      },
      partInfo: {
        partNumber: 'POR-GT3RS-AW',
        description: 'Aerodynamic Wing Kit',
        supplier: 'Porsche Centre SA',
      },
    },
    {
      id: 'AL004',
      type: 'shipping',
      title: 'Shipping Delay',
      description: 'Customs clearance delay for titanium exhaust system',
      timestamp: new Date(2024, 2, 15, 13, 20),
      priority: 'medium',
      status: 'new',
      vehicle: {
        make: 'Lamborghini',
        model: 'Aventador SVJ',
        year: 2022,
      },
      partInfo: {
        partNumber: 'LAM-SVJ-EXH',
        description: 'Titanium Exhaust System',
        supplier: 'Lamborghini Johannesburg',
      },
    },
    {
      id: 'AL005',
      type: 'critical',
      title: 'Urgent: Recall Notice',
      description: 'Manufacturer recall for ECU firmware update',
      timestamp: new Date(2024, 2, 15, 14, 30),
      priority: 'high',
      status: 'new',
      vehicle: {
        make: 'Bentley',
        model: 'Continental GT Speed',
        year: 2024,
      },
      partInfo: {
        partNumber: 'BEN-CGT-ECU-24',
        description: 'Engine Control Unit Firmware',
        supplier: 'Bentley Johannesburg',
      },
    },
  ];

  const getAlertIcon = (type: string) => {
    switch (type) {
      case 'price':
        return <PriceChangeIcon />;
      case 'stock':
        return <InventoryIcon />;
      case 'authentication':
        return <VerifiedUserIcon />;
      case 'shipping':
        return <ShippingIcon />;
      case 'critical':
        return <WarningIcon />;
      default:
        return <NotificationsIcon />;
    }
  };

  const getAlertColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'error';
      case 'medium':
        return 'warning';
      case 'low':
        return 'info';
      default:
        return 'default';
    }
  };

  const formatDateTime = (date: Date) => {
    return date.toLocaleString('en-ZA', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatPrice = (price: number) => {
    return `R ${price.toLocaleString()}`;
  };

  const filteredAlerts = alerts.filter(alert => {
    if (showOnlyUnread && alert.status !== 'new') return false;
    if (filterType === 'all') return true;
    return alert.type === filterType;
  });

  return (
    <DashboardLayout>
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Premium Parts Alerts Dashboard
        </Typography>

        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" color="error.main">
                  Critical Alerts
                </Typography>
                <Typography variant="h4">
                  {alerts.filter(a => a.priority === 'high' && a.status === 'new').length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Requiring immediate attention
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" color="warning.main">
                  Price Changes
                </Typography>
                <Typography variant="h4">
                  {alerts.filter(a => a.type === 'price' && a.status === 'new').length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Last 24 hours
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" color="info.main">
                  Authentication Pending
                </Typography>
                <Typography variant="h4">
                  {alerts.filter(a => a.type === 'authentication' && a.status === 'new').length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Certificates required
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Paper sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Alert Type</InputLabel>
                <Select
                  value={filterType}
                  label="Alert Type"
                  onChange={(e) => setFilterType(e.target.value)}
                >
                  <MenuItem value="all">All Alerts</MenuItem>
                  <MenuItem value="critical">Critical Alerts</MenuItem>
                  <MenuItem value="price">Price Changes</MenuItem>
                  <MenuItem value="stock">Stock Alerts</MenuItem>
                  <MenuItem value="authentication">Authentication</MenuItem>
                  <MenuItem value="shipping">Shipping Updates</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={showOnlyUnread}
                    onChange={(e) => setShowOnlyUnread(e.target.checked)}
                  />
                }
                label="Show only unread alerts"
              />
            </Grid>
          </Grid>
        </Paper>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Type</TableCell>
                <TableCell>Alert Details</TableCell>
                <TableCell>Vehicle</TableCell>
                <TableCell>Part Information</TableCell>
                <TableCell>Priority</TableCell>
                <TableCell>Timestamp</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredAlerts.map((alert) => (
                <TableRow key={alert.id}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {getAlertIcon(alert.type)}
                      <Typography variant="body2">
                        {alert.type.charAt(0).toUpperCase() + alert.type.slice(1)}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="subtitle2">{alert.title}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {alert.description}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="subtitle2">
                      {alert.vehicle.make} {alert.vehicle.model}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {alert.vehicle.year}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="subtitle2">
                      {alert.partInfo.description}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {alert.partInfo.partNumber}
                    </Typography>
                    {alert.type === 'price' && alert.partInfo.previousPrice && alert.partInfo.newPrice && (
                      <Typography variant="body2" color="error.main">
                        {formatPrice(alert.partInfo.previousPrice)} → {formatPrice(alert.partInfo.newPrice)}
                      </Typography>
                    )}
                    {alert.type === 'stock' && alert.partInfo.stockLevel !== undefined && (
                      <Typography variant="body2" color="warning.main">
                        Stock: {alert.partInfo.stockLevel} units
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={alert.priority.toUpperCase()}
                      color={getAlertColor(alert.priority)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {formatDateTime(alert.timestamp)}
                  </TableCell>
                  <TableCell>
                    <IconButton size="small" color="primary">
                      <CheckCircleIcon />
                    </IconButton>
                    <IconButton size="small" color="error">
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </DashboardLayout>
  );
}; 